import { useState } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = ({ chapters, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('surahs');
  const { language, changeLanguage, t } = useLanguage();

  // Juz data - mapping of Juz to starting surah
  const juzData = [
    { juz: 1, startSurah: 1, name: 'Juz 1 - Al-Fatihah' },
    { juz: 2, startSurah: 2, name: 'Juz 2 - Sayaqūlu' },
    { juz: 3, startSurah: 2, name: 'Juz 3 - Tilkal Rusulu' },
    { juz: 4, startSurah: 3, name: 'Juz 4 - Lan Tanālū' },
    { juz: 5, startSurah: 4, name: 'Juz 5 - Wal Muḥṣanāt' },
    { juz: 6, startSurah: 4, name: 'Juz 6 - Lā Yuḥibbullāh' },
    { juz: 7, startSurah: 5, name: 'Juz 7 - Wa Idhā Samiʿū' },
    { juz: 8, startSurah: 6, name: 'Juz 8 - Wa Law Annanā' },
    { juz: 9, startSurah: 7, name: 'Juz 9 - Qālal Malā' },
    { juz: 10, startSurah: 8, name: 'Juz 10 - Wa Aʿlamū' },
    { juz: 11, startSurah: 9, name: 'Juz 11 - Yaʿtadhirūna' },
    { juz: 12, startSurah: 11, name: 'Juz 12 - Wa Mā Min Dābbah' },
    { juz: 13, startSurah: 12, name: 'Juz 13 - Wa Mā Ubarriʾu' },
    { juz: 14, startSurah: 15, name: 'Juz 14 - Rubamā' },
    { juz: 15, startSurah: 17, name: 'Juz 15 - Subḥānal Ladhī' },
    { juz: 16, startSurah: 18, name: 'Juz 16 - Qāla Alā' },
    { juz: 17, startSurah: 21, name: 'Juz 17 - Iqtaraba' },
    { juz: 18, startSurah: 23, name: 'Juz 18 - Qad Aflaḥa' },
    { juz: 19, startSurah: 25, name: 'Juz 19 - Wa Qālalladhīna' },
    { juz: 20, startSurah: 27, name: 'Juz 20 - Amman Khalaqa' },
    { juz: 21, startSurah: 29, name: 'Juz 21 - Utlu Mā Ūḥiya' },
    { juz: 22, startSurah: 33, name: 'Juz 22 - Wa Man Yaqnut' },
    { juz: 23, startSurah: 36, name: 'Juz 23 - Wa Māliya' },
    { juz: 24, startSurah: 39, name: 'Juz 24 - Faman Aẓlamu' },
    { juz: 25, startSurah: 41, name: 'Juz 25 - Ilayhī Yuraddu' },
    { juz: 26, startSurah: 46, name: 'Juz 26 - Ḥā Mīm' },
    { juz: 27, startSurah: 51, name: 'Juz 27 - Qāla Fama' },
    { juz: 28, startSurah: 58, name: 'Juz 28 - Qad Samiʿa' },
    { juz: 29, startSurah: 67, name: 'Juz 29 - Tabārakalladhī' },
    { juz: 30, startSurah: 78, name: 'Juz 30 - ʿAmma' }
  ];

  const handleJuzClick = (startSurah) => {
    onNavigate(startSurah);
    setIsOpen(false);
  };

  const handleSurahClick = (surahId) => {
    onNavigate(surahId);
    setIsOpen(false);
  };

  return (
    <>
      {/* Header with Menu and Language Selector */}
      <div className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-[#E8EAE8] z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 text-[#3A5A40] hover:text-[#2B4330]"
            data-testid="menu-button"
          >
            <Menu size={24} strokeWidth={1.5} />
            <span className="font-medium">{t('quickNavigation')}</span>
          </button>

          <div className="flex items-center gap-3">
            <Globe size={20} className="text-[#6B726B]" />
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-[120px] border-[#E8EAE8] bg-white" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E8EAE8]">
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="ar">العربية</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-[72px]"></div>

      {/* Slide-in Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/20 z-50"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-[90%] max-w-md bg-white z-50 shadow-2xl overflow-hidden"
              data-testid="navigation-menu"
            >
              {/* Header */}
              <div className="bg-[#3A5A40] text-white p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-light" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('quickNavigation')}
                  </h2>
                  <button onClick={() => setIsOpen(false)} className="text-white hover:text-white/80">
                    <X size={24} />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTab('surahs')}
                    className={`pb-2 px-1 border-b-2 transition-colors ${
                      activeTab === 'surahs' ? 'border-white text-white' : 'border-transparent text-white/60'
                    }`}
                  >
                    {t('allSurahs')}
                  </button>
                  <button
                    onClick={() => setActiveTab('juz')}
                    className={`pb-2 px-1 border-b-2 transition-colors ${
                      activeTab === 'juz' ? 'border-white text-white' : 'border-transparent text-white/60'
                    }`}
                  >
                    {t('juz')}
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto h-[calc(100vh-180px)] p-4">
                {activeTab === 'surahs' ? (
                  <div className="space-y-2">
                    {chapters.map((chapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => handleSurahClick(chapter.id)}
                        className="w-full text-left p-3 rounded-lg hover:bg-[#F4F5F4] transition-colors"
                        data-testid={`menu-surah-${chapter.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#F4F5F4] flex items-center justify-center text-[#D4A373] text-sm font-medium">
                                {chapter.id}
                              </div>
                              <div>
                                <div className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                  {chapter.name_simple}
                                </div>
                                <div className="text-xs text-[#6B726B]">
                                  {chapter.verses_count} {t('ayahs')}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="text-xl arabic-text" style={{ fontFamily: 'Amiri, serif' }}>
                            {chapter.name_arabic}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {juzData.map((juz) => (
                      <button
                        key={juz.juz}
                        onClick={() => handleJuzClick(juz.startSurah)}
                        className="w-full text-left p-4 rounded-lg hover:bg-[#F4F5F4] transition-colors border border-[#E8EAE8]"
                        data-testid={`menu-juz-${juz.juz}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40] text-white flex items-center justify-center font-medium">
                            {juz.juz}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {juz.name}
                            </div>
                            <div className="text-xs text-[#6B726B] mt-1">
                              {t('allSurahs')} {juz.startSurah}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;